import { Link } from "react-router-dom"
import { Phone, Mail, MapPin, Linkedin, ShieldCheck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ibrApproval } from "@/data/credentials"
import { footerIndustryLinks, footerProductCategoryLinks } from "@/data/footer-links"

const footerLinks = {
  company: [
    { name: "About Us", path: "/about" },
    { name: "Products", path: "/products" },
    { name: "Projects", path: "/projects" },
    { name: "Blogs", path: "/blogs" },
  ],
}

export function Footer() {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-slate-100 text-slate-900 border-t border-slate-200">
      <div className="container mx-auto px-4">
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-10">
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img
                src="/images/logo_wide.png"
                alt="Maxitherm Works"
                width={240}
                height={48}
                className="h-12 w-auto"
              />
            </Link>
            <p className="text-slate-600 text-sm leading-relaxed mb-6">
              Maxitherm Works is your trusted partner for sustainable solutions in energy and
              environment. As a Thermax Channel Associate, we deliver quality engineering for
              industrial heating, cooling, and water treatment needs.
            </p>
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-800">
                <ShieldCheck className="h-3.5 w-3.5 text-maxi-red" />
                {ibrApproval.label}
              </div>
              <Link
                to="/about#approvals"
                className="mt-2 block text-xs text-slate-500 hover:text-slate-700 transition-colors"
              >
                View approval details and verification process
              </Link>
            </div>
            <div className="flex gap-3">
              <a
                href="https://www.linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit Maxitherm on LinkedIn"
                className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center hover:bg-maxi-red hover:text-white transition-colors"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          <div>
            <h2 className="font-semibold text-lg mb-4">Products</h2>
            <ul className="grid grid-cols-1 gap-y-2">
              {footerProductCategoryLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.path}
                    className="text-slate-600 hover:text-slate-900 transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-semibold text-lg mb-4">Company</h2>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-600 hover:text-slate-900 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-semibold text-lg mb-4">Industries</h2>
            <ul className="grid grid-cols-1 gap-y-2">
              {footerIndustryLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.path}
                    className="text-slate-600 hover:text-slate-900 transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-semibold text-lg mb-4">Contact</h2>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <MapPin className="h-5 w-5 text-maxi-red shrink-0" />
                <span className="text-slate-600 text-sm">
                  Cannought Garden, Chhatrapati Sambhajinagar (formerly known as Aurangabad),
                  Maharashtra 431001, India
                </span>
              </li>
              <li>
                <a
                  href="tel:+919404679370"
                  className="flex gap-3 text-slate-600 hover:text-slate-900 transition-colors text-sm"
                >
                  <Phone className="h-5 w-5 text-maxi-red shrink-0" />
                  +91 94046 79370
                </a>
              </li>
              <li>
                <a
                  href="mailto:sales@maxithermworks.com"
                  className="flex gap-3 text-slate-600 hover:text-slate-900 transition-colors text-sm"
                >
                  <Mail className="h-5 w-5 text-maxi-red shrink-0" />
                  sales@maxithermworks.com
                </a>
              </li>
            </ul>
            <div className="mt-6">
              <Button variant="cta" className="w-full" asChild>
                <a href="https://wa.me/919404679370" target="_blank" rel="noopener noreferrer">
                  WhatsApp Us
                </a>
              </Button>
            </div>
          </div>
        </div>

        <div className="py-6 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-600 text-sm">
            © {currentYear} Maxitherm Works. All rights reserved.
          </p>
          <p className="text-slate-600 text-sm flex items-center gap-1">
            Made with <span className="text-red-500">♥</span> in California
          </p>
        </div>
      </div>
    </footer>
  )
}
